import React, { useEffect, useState } from "react";
import "../styles/TaskAnalyticsReport.css";
import DataGridTable from "./table/DataGridTable";
import { FaDownload } from "react-icons/fa";
import axios from "axios";
import { FadeLoader } from "react-spinners";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import translations from "../assets/locals/translations";

const TaskAnalyticsReport = () => {
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [outcomeOptions, setOutcomeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Dummy data set
  //   const dummyData = [
  //     {
  //       indexID: 1,
  //       employee_name: "John Doe",
  //       customer_name: "Acme Corp",
  //       task_name: "Inspection",
  //       outcome_status: "Completed",
  //       attempt_date: "2023-01-15",
  //       notes: "Inspection completed successfully.",
  //     },
  //     {
  //       indexID: 2,
  //       employee_name: "Jane Smith",
  //       customer_name: "Beta LLC",
  //       task_name: "Maintenance",
  //       outcome_status: "Declined",
  //       attempt_date: "2023-02-10",
  //       notes: "Maintenance request declined.",
  //     },
  //     {
  //       indexID: 3,
  //       employee_name: "Alice Johnson",
  //       customer_name: "Gamma Inc",
  //       task_name: "Installation",
  //       outcome_status: "Completed",
  //       attempt_date: "2023-03-05",
  //       notes: "Installation completed with minor issues.",
  //     },
  //     {
  //       indexID: 4,
  //       employee_name: "Bob Brown",
  //       customer_name: "Delta Ltd",
  //       task_name: "Inspection",
  //       outcome_status: "Disconnected",
  //       attempt_date: "2023-03-20",
  //       notes: "Lost connection during inspection.",
  //     },
  //     {
  //       indexID: 5,
  //       employee_name: "John Doe",
  //       customer_name: "Acme Corp",
  //       task_name: "Maintenance",
  //       outcome_status: "Other",
  //       attempt_date: "2023-04-01",
  //       notes: "Unexpected result, pending follow-up.",
  //     },
  //   ];

  // Table columns definition
  const columns = [
    { field: "indexID", headerName: t.id || "ID", width: 90 },
    {
      field: "employee_name",
      headerAlign: "left",
      align: "left",
      headerName: t.employeeName || "Employee Name",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "category_name",
      headerAlign: "left",
      align: "left",
      headerName: t.categoryName || "Category Name",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "customer_name",
      headerAlign: "left",
      align: "left",
      headerName: t.customerName || "Customer Name",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "phone_number",
      headerAlign: "left",
      align: "left",
      headerName: t.phoneNumber || "Phone Number",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "village",
      headerAlign: "left",
      align: "left",
      headerName: t.village || "Village",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "task_name",
      headerAlign: "left",
      align: "left",
      headerName: t.taskName || "Task Name",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "outcome_status",
      headerAlign: "left",
      align: "left",
      headerName: t.outcomeStatus || "Outcome Status",
      minWidth: 200,
      flex: 2,
    },
    {
      field: "attempt_date",
      headerAlign: "left",
      align: "left",
      headerName: t.attemptDate || "Attempt Date",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "notes",
      headerAlign: "left",
      align: "left",
      headerName: t.notes || "Notes",
      minWidth: 300,
      flex: 2,
    },
  ];

  async function getOptionsData() {
    try {
      const url = `${process.env.REACT_APP_NODE_URL}/api/report/get-options-data`;
      const config = {
        headers: {
          token: localStorage.getItem("rbacToken"),
        },
      };

      const response = await axios.get(url, config);
      if (response.data.success) {
        const { data } = response.data;
        setEmployeeOptions([
          { value: "-1", name: "All" },
          ...data.employeeOptions,
        ]);
        setCategoryOptions([
          { id: "-1", category_name: "All" },
          ...data.categoryOptions,
        ]);
        setOutcomeOptions([
          { value: "ALL", name: "All" },
          ...data.outcomeOptions,
        ]);
      }
    } catch (error) {
      console.error("Failed to fetch options data:", error);
    }
  }
  async function getAnalyticsData(payload) {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_NODE_URL}/api/report/get-analytics-report`;
      const config = {
        headers: {
          token: localStorage.getItem("rbacToken"),
        },
      };

      const response = await axios.post(url, payload, config);
      if (response.data.success) {
        return response.data.data;
      } else {
        console.error("Error fetching report:", response.data.message);
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch completed tasks:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getOptionsData();
  }, []);
  // State for table data and filter fields
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    employee: "",
    category: "",
    outcome: "",
    startDate: "",
    endDate: "",
  });

  // Options for dropdowns extracted from dummy data
  //   const employeeOptions = [
  //     ...new Set(dummyData.map((item) => item.employee_name)),
  //   ];
  //   const categoryOptions = [...new Set(dummyData.map((item) => item.task_name))];
  //   const outcomeOptions = ["Completed", "Disconnected", "Declined", "Other"];

  // Update filter state when any input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply filters on form submission
  //   const handleSubmit = (e) => {
  //     e.preventDefault();
  //     let filtered = taskData;

  //     if (filters.employee) {
  //       filtered = filtered.filter(
  //         (item) => item.employee_name === filters.employee
  //       );
  //     }
  //     if (filters.category) {
  //       filtered = filtered.filter((item) => item.task_name === filters.category);
  //     }
  //     if (filters.outcome) {
  //       filtered = filtered.filter(
  //         (item) => item.outcome_status === filters.outcome
  //       );
  //     }
  //     if (filters.startDate) {
  //       filtered = filtered.filter(
  //         (item) => new Date(item.attempt_date) >= new Date(filters.startDate)
  //       );
  //     }
  //     if (filters.endDate) {
  //       filtered = filtered.filter(
  //         (item) => new Date(item.attempt_date) <= new Date(filters.endDate)
  //       );
  //     }
  //     setFilteredData(filtered);
  //   };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Construct payload using values from filters.
    // Make sure the types are as expected by your stored procedure.
    const payload = {
      employeeId: filters.employee,
      categoryId: filters.category,
      outComeStatus: filters.outcome,
      startDate: filters.startDate,
      endDate: filters.endDate,
    };

    // Call the API and update the table data
    const res = await getAnalyticsData(payload);
    const rowsData = res.map((item, index) => ({
      ...item,
      attempt_date: moment(item.attempt_date)
        .tz("Asia/Kolkata")
        .format("YYYY-MM-DD HH:mm:ss"),
      indexID: index + 1,
    }));
    setFilteredData(rowsData);
  };

  // CSV Export Function using filtered data
  const downloadCSV = () => {
    if (filteredData.length === 0) return;

    const employeePart =
      filters.employee && filters.employee !== "-1"
        ? filteredData[0]?.employee_name || "Employee"
        : "All_Employees";

    // For category: if a specific category is selected (not "-1"), use its name from the first row;
    // otherwise use "All_Categories".
    const categoryPart =
      filters.category && filters.category !== "-1"
        ? filteredData[0]?.category_name || "Category"
        : "All_Categories";

    // For outcome: if a specific outcome is selected (not "ALL"), include it; otherwise "All_Outcomes".
    const outcomePart =
      filters.outcome && filters.outcome !== "ALL"
        ? filters.outcome
        : "All_Outcomes";

    // For dates: if valid dates are provided (not "0000-00-00"), include the range.
    let datePart = "";
    if (
      filters.startDate &&
      filters.startDate !== "0000-00-00" &&
      filters.endDate &&
      filters.endDate !== "0000-00-00"
    ) {
      datePart = `${filters.startDate}_to_${filters.endDate}`;
    }

    // Combine the parts (only add datePart if it exists)
    const parts = [employeePart, categoryPart, outcomePart];
    if (datePart) parts.push(datePart);
    let filename = parts.join("_") + "_Task_Completion_Report.csv";

    // Sanitize the filename to remove unwanted characters.
    filename = filename.replace(/[^a-zA-Z0-9_-]/g, "");

    // Build CSV header using column headers
    const csvHeader =
      columns.map((col) => `"${col.headerName}"`).join(",") + "\r\n";

    // Format CSV rows using filtered data
    const csvRows = filteredData.map((row) =>
      columns.map((col) => `"${row[col.field] || ""}"`).join(",")
    );

    // Combine header and rows with UTF-8 BOM for compatibility
    const csvContent = "\uFEFF" + csvHeader + csvRows.join("\r\n");

    // Create a Blob from the CSV and trigger a download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handler for row selection (from DataGridTable)
  const handleSelectedRowsChange = (selectedRows) => {
    console.log("Selected rows:", selectedRows);
  };

  return (
    <div className="bg-white">
      {/* Filter Section */}
      <div className="filter-card">
        <form onSubmit={handleSubmit} className="filter-form">
          <div className="filter-group">
            <label htmlFor="employee">{t.employee || "Employee"}</label>
            <select
              name="employee"
              id="employee"
              value={filters.employee}
              onChange={handleFilterChange}
            >
              <option value="">{t.selectEmployee || "Select Employee"}</option>
              {employeeOptions.map((emp) => (
                <option key={emp.value} value={emp.value}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="category">{t.category || "Category"}</label>
            <select
              name="category"
              id="category"
              value={filters.category}
              onChange={handleFilterChange}
            >
              <option value="">{t.selectCategory || "Select Category"}</option>
              {categoryOptions.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.category_name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="outcome">{t.outcomeStatus || "Outcome Status"}</label>
            <select
              name="outcome"
              id="outcome"
              value={filters.outcome}
              onChange={handleFilterChange}
            >
              <option value="">{t.selectStatus || "Select Status"}</option>
              {outcomeOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="startDate">{t.startDate || "Start Date"}</label>
            <input
              type="date"
              name="startDate"
              id="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="endDate">{t.endDate || "End Date"}</label>
            <input
              type="date"
              name="endDate"
              id="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="button-group">
            <button type="submit" className="filter-button">
              {t.submit || "Submit"}
            </button>
            <button onClick={downloadCSV} className="filter-button ms-1">
              <FaDownload style={{ marginRight: "8px" }} />
              {t.download || "Download"}
            </button>
          </div>
        </form>
      </div>
      {/* Table Section */}
      {loading ? (
        <div
          className="container"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <FadeLoader size={10} color="#808b96" />
          <span
            style={{
              // marginTop: "10px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              color: "#808b96",
            }}
          >
            {t.loadingPleaseWait || "Loading, please wait..."}
          </span>
        </div>
      ) : (
        <DataGridTable
          data={filteredData}
          columns={columns}
          onCheckboxChange={handleSelectedRowsChange}
        />
      )}
    </div>
  );
};

export default TaskAnalyticsReport;
